import { useEffect, useState } from 'react';
import { useCuttingStore } from './state/useCuttingStore';
import { ShareFormatDialog } from './dialogs/ShareFormatDialog';

/**
 * Block 14 «Раскрой» — действия экрана «Результат». Вертикальный список кнопок
 * (P-019), рендерится ТОЛЬКО при success-результате (`CutMap`) — без раскроя
 * действовать не над чем. Тексты/палитра — cutting-theme (`--text-primary`,
 * P-039/P-045); tap-target `.btn` `min-height: 44px` (P-044).
 *
 * Кнопки (порядок прототипа project.html:512-516):
 *  1. 💾 «Сохранить проект» (PR #25, EP-5): персист текущего документа ВКЛЮЧАЯ
 *     результат раскроя (`lastResult` → `data.lastResult`) через `saveCurrentProject`
 *     (идемпотентно по `currentProjectId`). Пустой материал (D2 `min(1)`) гейтится
 *     inline-сообщением ДО сети (честное error-состояние, P-021). Фидбэк — inline
 *     `aria-live` (idle/loading/success/error), без `alert()`.
 *  2. 🖨 «Распечатать карту кроя» (PR #25): `window.print()`; print-CSS
 *     (`@media print`, cutting-theme.css) печатает ТОЛЬКО `.cutting-print-region`
 *     (метрики + текущий лист карты), хром/пагер/кнопки скрыты.
 *  3. 📦 «Сохранить остатки» (PR #24-B): новые годные остатки → склад. Inline
 *     `aria-live` статус (§3.6), идемпотентный дизейбл (§3.4).
 *  4. ✂ «Отправить на распил» (PR share-cut-pdf): открывает модалку выбора формата
 *     `ShareFormatDialog` — PDF карты раскроя ОТПРАВЛЯЕТСЯ через Web Share API
 *     (системное окно «Поделиться», по образцу КП `shareQuotePdf`), Excel/CSV —
 *     СКАЧИВАЮТСЯ. Форматы (PDF/Excel/CSV) живут ТОЛЬКО в модалке — она самодостаточна
 *     (строит файлы сама), отдельных кнопок экспорта на экране нет (убраны как дубль).
 *     Дизейблится без результата / при пустом списке деталей.
 *
 * (🏷 Этикетки — будущий PR.)
 */
export function ResultActions() {
  const lastResult = useCuttingStore((s) => s.lastResult);
  const cutStatus = useCuttingStore((s) => s.cutStatus);
  const materialText = useCuttingStore((s) => s.document.meta.materialText);
  const doc = useCuttingStore((s) => s.document);
  const saveCurrentProject = useCuttingStore((s) => s.saveCurrentProject);
  const saved = useCuttingStore((s) => s.resultOffcutsSaved);
  const saveResultOffcuts = useCuttingStore((s) => s.saveResultOffcutsToWarehouse);

  const [offcutsStatus, setOffcutsStatus] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Новый раскрой (смена lastResult) → сбросить прошлые inline-статусы, иначе
  // «Сохранено…» / «Проект сохранён» от прошлого результата висит над активными кнопками.
  useEffect(() => {
    setOffcutsStatus('');
    setSaveStatus('');
  }, [lastResult]);

  if (cutStatus !== 'success' || !lastResult) return null;

  const handleSaveProject = async () => {
    if (!materialText.trim()) {
      // D2: материал обязателен — гейтим до сети (честное validation-состояние, P-021).
      setSaveStatus('Укажите материал на вкладке «Настройки»');
      return;
    }
    setSaving(true);
    setSaveStatus('Сохранение…'); // loading
    try {
      await saveCurrentProject();
      setSaveStatus('Проект сохранён');
    } catch {
      setSaveStatus('Не удалось сохранить проект. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    // Печать карты кроя: print-CSS (@media print) скрывает весь хром, оставляя
    // только .cutting-print-region (метрики + текущий лист). Без загрузки на сервер.
    window.print();
  };

  const handleSaveOffcuts = () => {
    const n = saveResultOffcuts();
    setOffcutsStatus(
      n > 0 ? `Сохранено ${n} остатков в склад` : 'Годных остатков для сохранения нет',
    );
  };

  // Равные пробелы (#168-A #5): каждая кнопка — блок `.sc-result-action` с
  // зарезервированным status-слотом одинаковой высоты (у «Печать» — пустой,
  // aria-hidden), чтобы статус появлялся в своём слоте, не раздвигая соседей.
  return (
    <div className="sc-result-actions">
      <div className="sc-result-action">
        <button
          type="button"
          className="btn btn-success btn-lg"
          onClick={() => void handleSaveProject()}
          disabled={saving}
        >
          {saving ? '💾 Сохранение…' : '💾 Сохранить проект'}
        </button>
        <p className="sc-result-actions-status" role="status" aria-live="polite">
          {saveStatus}
        </p>
      </div>

      <div className="sc-result-action">
        <button type="button" className="btn btn-secondary btn-lg" onClick={handlePrint}>
          🖨 Распечатать карту кроя
        </button>
        {/* пустой зарезервированный слот — для равной высоты блока (P-019 ритм) */}
        <p className="sc-result-actions-status" aria-hidden="true" />
      </div>

      <div className="sc-result-action">
        <button
          type="button"
          className="btn btn-secondary btn-lg"
          onClick={handleSaveOffcuts}
          disabled={saved}
        >
          {saved ? '✅ Остатки сохранены' : '📦 Сохранить остатки'}
        </button>
        <p className="sc-result-actions-status" role="status" aria-live="polite">
          {offcutsStatus}
        </p>
      </div>

      <div className="sc-result-action">
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={() => setShareOpen(true)}
          disabled={!lastResult || doc.parts.length === 0}
        >
          ✂ Отправить на распил
        </button>
        {/* пустой зарезервированный слот — статус живёт внутри модалки (P-019 ритм) */}
        <p className="sc-result-actions-status" aria-hidden="true" />
      </div>

      {shareOpen && (
        <ShareFormatDialog doc={doc} result={lastResult} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}
